import { Box, Modal } from '@fullstory/components';
import { intl } from '@fullstory/intl';
import { Space } from '@fullstory/proto/fs/services/dx-objects';
import { LibraryObjectType } from '@fullstory/routes';
import React, { PropsWithChildren } from 'react';
import { LibrarySearchBar } from '../LibrarySearchBar';
import { ObjectTagFilters } from '../ObjectTagFilters';

const ORDERED_FILTER_TYPES = ['active', 'starred', 'mine', 'private'] as const;
type SupportedFilter = (typeof ORDERED_FILTER_TYPES)[number];

interface AddFromLibraryModalProps
  extends Pick<React.ComponentProps<typeof Modal>, 'open' | 'size' | 'onCloseSignal'> {
  /**
   * Title of the Modal
   *
   * @default Add from library
   */
  title?: string;
  /**
   * The selected filter.
   */
  filter: SupportedFilter;
  onFilterChange: (filter: SupportedFilter) => void;
  /**
   * The list of searchable Spaces available for selection.
   */
  spaces: Space[];
  /**
   * null means no Space is selected, it is the All option.
   */
  selectedSpace: Space | null | undefined;
  /**
   * null means no Space to filter results on, it is the All option.
   */
  onSpaceChange: (space: Space | null) => void;
  /**
   * The enabled object types that can be used to filter.
   */
  enabledObjectTypes: LibraryObjectType[];
  /**
   * null means All enabled object types.
   */
  selectedObjectType: LibraryObjectType | null;
  /**
   * null means no Space to filter results on, it is the All option.
   */
  onObjectTypeChange: (types: LibraryObjectType | null) => void;
  search: string | null | undefined;
  onSearchChange: (search: string) => void;
}

export const AddFromLibraryModal: React.FC<PropsWithChildren<AddFromLibraryModalProps>> = ({
  open,
  title = intl.formatMessage({ defaultMessage: 'Add from library', id: '2I9hVB' }),
  size,
  filter,
  onFilterChange,
  spaces,
  selectedSpace,
  onSpaceChange,
  enabledObjectTypes,
  selectedObjectType,
  onObjectTypeChange,
  search,
  onSearchChange,
  onCloseSignal,
  children,
}) => (
  <Modal data-component="AddFromLibraryModal" open={open} size={size} onCloseSignal={onCloseSignal}>
    <Modal.Header onCloseSignal={onCloseSignal}>{title}</Modal.Header>
    <Box padding={[20, 20, 24, 20]}>
      <LibrarySearchBar<SupportedFilter>
        filterTypes={ORDERED_FILTER_TYPES}
        filter={filter}
        onFilterChange={onFilterChange}
        search={search ?? ''}
        onSearchChange={onSearchChange}
        spaces={{
          availableSpaces: spaces,
          selectedSpace: selectedSpace ?? null,
          onSelectedSpaceChange: onSpaceChange,
        }}
        selectMenuSize="medium"
        selectMenuVariant="fill-builder"
        showLabels
      />
      {enabledObjectTypes.length > 1 && (
        <Box marginTop={24}>
          <ObjectTagFilters
            objectTypes={enabledObjectTypes}
            selectedObjectType={selectedObjectType}
            onSelectedObjectTypeChange={onObjectTypeChange}
            location="Modal"
          />
        </Box>
      )}
    </Box>
    {children}
  </Modal>
);
